import React, { useEffect, useState, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  HStack,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Spinner,
  Alert,
  AlertIcon,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  CardFooter
} from '@chakra-ui/react';
import { FaFolder, FaFileAlt, FaChevronRight, FaArrowRight } from 'react-icons/fa';
import { s3Service } from '../services/api';
import { AuthContext } from '../context/AuthContext';

function Dashboard() {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPrefix, setCurrentPrefix] = useState('');
  // const { user } = useContext(AuthContext); // Not using user in this component
  const location = useLocation();
  const navigate = useNavigate();

  // Effect to handle initial load and URL state
  useEffect(() => {
    // Check if there's a folder parameter in the URL
    const params = new URLSearchParams(location.search);
    const folderParam = params.get('folder');
    
    if (folderParam) {
      // Decode the folder path from URL
      const decodedPrefix = decodeURIComponent(folderParam);
      setCurrentPrefix(decodedPrefix);
    } else {
      // No folder in URL, start from root
      fetchData();
    }
  }, [location.search]);
  
  // Effect to fetch data when currentPrefix changes
  useEffect(() => {
    fetchData();
    
    // Update URL with current folder (except for root)
    if (currentPrefix) {
      navigate({
        pathname: location.pathname,
        search: `?folder=${encodeURIComponent(currentPrefix)}`
      }, { replace: true });
    } else if (location.search) {
      // Clear URL parameters when at root
      navigate({ pathname: location.pathname }, { replace: true });
    }
  }, [currentPrefix]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Always fetch folders
      const foldersResponse = await s3Service.listFolders(currentPrefix);
      setFolders(foldersResponse.folders);
      
      // Only fetch .env files if we're inside a folder (currentPrefix is not empty)
      if (currentPrefix) {
        const filesResponse = await s3Service.listEnvFiles(currentPrefix);
        setFiles(filesResponse.files);
      } else {
        // Clear files when at root level
        setFiles([]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = (prefix) => {
    setCurrentPrefix(prefix);
  };

  const navigateUp = () => {
    // Remove the last folder from the path
    const parts = currentPrefix.split('/');
    parts.pop(); // Remove the last empty string
    if (parts.length > 0) {
      parts.pop(); // Remove the last folder
    }
    setCurrentPrefix(parts.length > 0 ? parts.join('/') + '/' : '');
  };

  const renderBreadcrumbs = () => {
    if (!currentPrefix) return null;
    
    const parts = currentPrefix.split('/').filter(Boolean);
    
    return (
      <Breadcrumb separator={<FaChevronRight color="gray.500" />} mb={4}>
        <BreadcrumbItem>
          <BreadcrumbLink onClick={() => setCurrentPrefix('')}>Root</BreadcrumbLink>
        </BreadcrumbItem>
        
        {parts.map((part, index) => {
          const path = parts.slice(0, index + 1).join('/') + '/';
          return (
            <BreadcrumbItem key={path} isCurrentPage={index === parts.length - 1}>
              <BreadcrumbLink onClick={() => setCurrentPrefix(path)}>
                {part}
              </BreadcrumbLink>
            </BreadcrumbItem>
          );
        })}
      </Breadcrumb>
    );
  };

  return (
    <Box>
      <Box mb={3}>
        <Heading as="h1" size="lg" mb={1}>Environment Files</Heading>
        <Text fontSize="sm">Browse and manage your .env files</Text>
      </Box>
      
      <Box mb={2}>{renderBreadcrumbs()}</Box>
      
      {currentPrefix && (
        <Button leftIcon={<FaChevronRight transform="rotate(180deg)" />} onClick={navigateUp} mb={2} size="sm">
          Up one level
        </Button>
      )}
      
      {loading ? (
        <Box textAlign="center" py={10}>
          <Spinner size="xl" />
          <Text mt={4}>Loading...</Text>
        </Box>
      ) : error ? (
        <Alert status="error" mb={6} rounded="md">
          <AlertIcon />
          {error}
        </Alert>
      ) : (
        <>
          {folders.length === 0 && files.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Text>No folders or .env files found in this location.</Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
              {folders.map((folder, index) => {
                const folderName = folder.split('/').filter(Boolean).pop();
                return (
                  <Card 
                    key={`folder-${index}`} 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigateToFolder(folder)}
                    cursor="pointer"
                    _hover={{ boxShadow: 'md', transform: 'translateY(-2px)', transition: 'all 0.2s ease-in-out' }}
                  >
                    <CardHeader py={3} px={4}>
                      <HStack justifyContent="space-between">
                        <HStack>
                          <FaFolder color="gold" />
                          <Heading size="md">{folderName}</Heading>
                        </HStack>
                        <FaArrowRight size="0.8em" opacity={0.7} />
                      </HStack>
                    </CardHeader>
                  </Card>
                );
              })}
              
              {files.map((file, index) => {
                const fileName = file.split('/').pop();
                return (
                  <Card 
                    key={`file-${index}`} 
                    variant="outline" 
                    size="sm"
                    as={Link}
                    to={`/env-file/${encodeURIComponent(file)}`}
                    cursor="pointer"
                    _hover={{ boxShadow: 'md', transform: 'translateY(-2px)', transition: 'all 0.2s ease-in-out' }}
                    textDecoration="none"
                  >
                    <CardHeader py={3} px={4}>
                      <HStack justifyContent="space-between">
                        <HStack>
                          <FaFileAlt color="green" />
                          <Heading size="md">{fileName}</Heading>
                        </HStack>
                        <FaArrowRight size="0.8em" opacity={0.7} />
                      </HStack>
                    </CardHeader>
                  </Card>
                );
              })}
            </SimpleGrid>
          )}
        </>
      )}
    </Box>
  );
}

export default Dashboard;
